import React from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Container,
  Paper,
  useTheme,
  useMediaQuery,
  Grid,
  Avatar,
  Divider,
  Tooltip,
  IconButton,
  Snackbar,
  Alert
} from '@mui/material';
import { 
  CheckCircle, 
  Star, 
  CardGiftcard, 
  HeadsetMic, 
  Share,
  ArrowForward,
  Facebook,
  Twitter,
  LinkedIn,
  WhatsApp,
  Email
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';

const SuccessCheckoutPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [openSnackbar, setOpenSnackbar] = React.useState(false);
  const [setCopied] = React.useState(false);

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  const handleShare = (platform) => {
    const shareUrl = 'https://seusite.com/premium';
    const shareText = 'Acabei de assinar o plano Premium Pro e estou amando os benefícios!';

    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, '_blank');
        break;
      case 'linkedin':
        window.open(`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareText)}`, '_blank');
        break;
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`, '_blank');
        break;
      case 'email':
        window.open(`mailto:?subject=Assinei o Premium Pro&body=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`);
        break;
      case 'copy':
        navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        setOpenSnackbar(true);
        setTimeout(() => setCopied(false), 3000);
        break;
      default:
        break;
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        display: 'flex',
        alignItems: 'center',
        py: 8,
        position: 'relative',
        overflow: 'hidden',
        '&:before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 50%, rgba(76, 175, 80, 0.1) 0%, transparent 30%)',
          zIndex: 0
        }
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <SuccessCard elevation={3}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <SuccessIcon>
              <CheckCircle fontSize="inherit" />
            </SuccessIcon>
            <Typography 
              variant={isMobile ? 'h4' : 'h3'} 
              component="h1" 
              sx={{ 
                fontWeight: 700,
                color: theme.palette.success.main,
                mt: 2,
                textShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              Parabéns!
            </Typography>
            <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ 
              mt: 1, 
              color: theme.palette.text.secondary,
              maxWidth: '80%',
              mx: 'auto'
            }}>
              Seu plano Premium foi ativado com sucesso
            </Typography>
          </Box>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ 
                p: 3, 
                height: '100%', 
                background: 'rgba(255,255,255,0.8)',
                border: `1px solid ${theme.palette.divider}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: theme.shadows[6]
                }
              }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Resumo do Seu Plano
                </Typography>
                
                <PlanDetail>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Plano Premium Pro
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                    €19,00/mês
                  </Typography>
                </PlanDetail>
                
                <Divider sx={{ my: 2 }} />
                
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Seu acesso premium está ativo imediatamente. Aproveite todos os benefícios:
                </Typography>
                
                <FeatureItem>
                  <Star color="primary" sx={{ mr: 1 }} />
                  <Typography>Destaque nos resultados de busca</Typography>
                </FeatureItem>
                
                <FeatureItem>
                  <Star color="primary" sx={{ mr: 1 }} />
                  <Typography>Leads ilimitados</Typography>
                </FeatureItem>
                
                <FeatureItem>
                  <Star color="primary" sx={{ mr: 1 }} />
                  <Typography>Analytics avançados</Typography>
                </FeatureItem>

                <FeatureItem>
                  <Star color="primary" sx={{ mr: 1 }} />
                  <Typography>Relatórios personalizados</Typography>
                </FeatureItem>

                <FeatureItem>
                  <Star color="primary" sx={{ mr: 1 }} />
                  <Typography>Suporte prioritário 24/7</Typography>
                </FeatureItem>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper sx={{ 
                p: 3, 
                height: '100%', 
                background: 'rgba(255,255,255,0.8)',
                border: `1px solid ${theme.palette.divider}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: theme.shadows[6]
                }
              }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Próximos Passos
                </Typography>
                
                <NextStepCard>
                  <Avatar sx={{ 
                    bgcolor: theme.palette.primary.main, 
                    mr: 2,
                    width: 48,
                    height: 48
                  }}>
                    <CardGiftcard fontSize="medium" />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                      Presente Especial
                    </Typography>
                    <Typography variant="body2">
                      Você recebeu um guia exclusivo para maximizar seus resultados.
                    </Typography>
                    <Button 
                      size="small" 
                      sx={{ mt: 1 }}
                      onClick={() => navigate('/resources/premium-guide')}
                    >
                      Baixar Guia
                    </Button>
                  </Box>
                </NextStepCard>
                
                <NextStepCard>
                  <Avatar sx={{ 
                    bgcolor: theme.palette.secondary.main, 
                    mr: 2,
                    width: 48,
                    height: 48
                  }}>
                    <HeadsetMic fontSize="medium" />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                      Suporte Prioritário
                    </Typography>
                    <Typography variant="body2">
                      Nosso time está disponível 24/7 para te ajudar.
                    </Typography>
                    <Button 
                      size="small" 
                      sx={{ mt: 1 }}
                      onClick={() => navigate('/support')}
                    >
                      Falar com Suporte
                    </Button>
                  </Box>
                </NextStepCard>

                <NextStepCard>
                  <Avatar sx={{ 
                    bgcolor: theme.palette.success.main, 
                    mr: 2,
                    width: 48,
                    height: 48
                  }}>
                    <ArrowForward fontSize="medium" />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                      Configuração Rápida
                    </Typography>
                    <Typography variant="body2">
                      Configure seu perfil premium em poucos minutos.
                    </Typography>
                    <Button 
                      size="small" 
                      sx={{ mt: 1 }}
                      onClick={() => navigate('/setup-premium')}
                    >
                      Iniciar Configuração
                    </Button>
                  </Box>
                </NextStepCard>
                
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    size="large"
                    endIcon={<ArrowForward />}
                    onClick={handleGoToDashboard}
                    sx={{
                      px: 4,
                      py: 1.5,
                      borderRadius: 2,
                      boxShadow: theme.shadows[4],
                      '&:hover': {
                        boxShadow: theme.shadows[8],
                        transform: 'translateY(-2px)'
                      },
                      fontSize: isMobile ? '0.875rem' : '1rem'
                    }}
                  >
                    Acessar Dashboard Premium
                  </Button>
                </Box>
              </Paper>
            </Grid>
          </Grid>
          
          <Box sx={{ 
            mt: 4, 
            textAlign: 'center',
            borderTop: `1px solid ${theme.palette.divider}`,
            pt: 3
          }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Compartilhe sua conquista com amigos
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
              <Tooltip title="Compartilhar no Facebook">
                <IconButton 
                  color="primary" 
                  onClick={() => handleShare('facebook')}
                  sx={{ bgcolor: '#1877f2', color: 'white', '&:hover': { bgcolor: '#166fe5' } }}
                >
                  <Facebook />
                </IconButton>
              </Tooltip>
              <Tooltip title="Compartilhar no Twitter">
                <IconButton 
                  color="primary" 
                  onClick={() => handleShare('twitter')}
                  sx={{ bgcolor: '#1da1f2', color: 'white', '&:hover': { bgcolor: '#1a91da' } }}
                >
                  <Twitter />
                </IconButton>
              </Tooltip>
              <Tooltip title="Compartilhar no LinkedIn">
                <IconButton 
                  color="primary" 
                  onClick={() => handleShare('linkedin')}
                  sx={{ bgcolor: '#0077b5', color: 'white', '&:hover': { bgcolor: '#00669c' } }}
                >
                  <LinkedIn />
                </IconButton>
              </Tooltip>
              <Tooltip title="Compartilhar no WhatsApp">
                <IconButton 
                  color="primary" 
                  onClick={() => handleShare('whatsapp')}
                  sx={{ bgcolor: '#25d366', color: 'white', '&:hover': { bgcolor: '#1fba5a' } }}
                >
                  <WhatsApp />
                </IconButton>
              </Tooltip>
              <Tooltip title="Compartilhar por Email">
                <IconButton 
                  color="primary" 
                  onClick={() => handleShare('email')}
                  sx={{ bgcolor: theme.palette.text.secondary, color: 'white', '&:hover': { bgcolor: theme.palette.text.primary } }}
                >
                  <Email />
                </IconButton>
              </Tooltip>
              <Tooltip title="Copiar link">
                <IconButton 
                  color="primary" 
                  onClick={() => handleShare('copy')}
                  sx={{ bgcolor: theme.palette.grey[700], color: 'white', '&:hover': { bgcolor: theme.palette.grey[800] } }}
                >
                  <Share />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </SuccessCard>
      </Container>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          Link copiado para a área de transferência!
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Componentes estilizados
const SuccessCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(6),
  borderRadius: theme.shape.borderRadius * 3,
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  boxShadow: theme.shadows[12],
  position: 'relative',
  overflow: 'hidden',
  '&:after': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    width: '100px',
    height: '100px',
    background: 'radial-gradient(circle, rgba(76, 175, 80, 0.1) 0%, transparent 70%)',
    zIndex: -1
  },
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(3),
    borderRadius: theme.shape.borderRadius * 2
  }
}));

const SuccessIcon = styled(Box)(({ theme }) => ({
  fontSize: '5rem',
  color: theme.palette.success.main,
  margin: '0 auto',
  animation: 'pulse 1.5s infinite alternate',
  '@keyframes pulse': {
    '0%': {
      transform: 'scale(1)',
      opacity: 1
    },
    '100%': {
      transform: 'scale(1.1)',
      opacity: 0.8
    }
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '3rem'
  }
}));

const PlanDetail = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(1),
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  background: theme.palette.grey[50],
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start'
  }
}));

const FeatureItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(1.5),
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  transition: 'all 0.2s ease',
  '&:hover': {
    background: theme.palette.action.hover,
    transform: 'translateX(5px)'
  }
}));

const NextStepCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  background: theme.palette.background.paper,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: theme.shadows[2],
    background: theme.palette.grey[50]
  }
}));

export default SuccessCheckoutPage;